import { ref, watch } from "vue";
import { http } from "@/api/http";
import { getApiUrl } from "@/data/constants";

export interface NameInputSettings {
  capitalizeNameField: boolean;
}

const DEFAULTS: NameInputSettings = {
  capitalizeNameField: true,
};

const LOCAL_ENABLED_KEY = "settings.nameInput.capitalizeNameField";
const LOCAL_USE_GLOBAL_KEY = "settings.nameInput.useGlobal";

function readStoredBoolean(key: string, fallback: boolean): boolean {
  const value = window.localStorage.getItem(key);
  if (value === null) return fallback;
  return value === "true";
}

const useGlobalNameInputSettings = ref<boolean>(
  readStoredBoolean(LOCAL_USE_GLOBAL_KEY, true)
);
const capitalizeNameField = ref<boolean>(
  readStoredBoolean(LOCAL_ENABLED_KEY, DEFAULTS.capitalizeNameField)
);

let hasLoaded = !useGlobalNameInputSettings.value;
let loadPromise: Promise<void> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function applySettings(settings: NameInputSettings) {
  capitalizeNameField.value = settings.capitalizeNameField;
}

async function loadNameInputSettings() {
  if (!useGlobalNameInputSettings.value) {
    hasLoaded = true;
    applySettings({
      capitalizeNameField: readStoredBoolean(
        LOCAL_ENABLED_KEY,
        DEFAULTS.capitalizeNameField
      ),
    });
    return;
  }
  if (loadPromise !== null) return loadPromise;
  loadPromise = http
    .get<NameInputSettings>(`${getApiUrl()}/settings/name-input`)
    .then((response) => {
      applySettings(response.data);
      hasLoaded = true;
    })
    .catch((error) => {
      console.error("Failed to load name input settings", error);
      hasLoaded = true;
    })
    .finally(() => {
      loadPromise = null;
    });
  return loadPromise;
}

async function saveNameInputSettings() {
  const settings: NameInputSettings = {
    capitalizeNameField: capitalizeNameField.value,
  };
  window.localStorage.setItem(
    LOCAL_ENABLED_KEY,
    String(settings.capitalizeNameField)
  );
  if (!useGlobalNameInputSettings.value) {
    return;
  }
  try {
    await http.put(`${getApiUrl()}/settings/name-input`, settings);
  } catch (error) {
    console.error("Failed to save name input settings", error);
  }
}

watch(useGlobalNameInputSettings, (useGlobal) => {
  window.localStorage.setItem(LOCAL_USE_GLOBAL_KEY, String(useGlobal));
  if (useGlobal) {
    hasLoaded = false;
    void loadNameInputSettings();
  } else {
    hasLoaded = true;
    applySettings({
      capitalizeNameField: readStoredBoolean(
        LOCAL_ENABLED_KEY,
        DEFAULTS.capitalizeNameField
      ),
    });
  }
});

watch(capitalizeNameField, () => {
  window.localStorage.setItem(LOCAL_ENABLED_KEY, String(capitalizeNameField.value));
  if (!hasLoaded) return;
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
  }
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void saveNameInputSettings();
  }, 250);
});

export function useNameInputSettings() {
  void loadNameInputSettings();
  return {
    capitalizeNameField,
    useGlobalNameInputSettings,
    loadNameInputSettings,
    saveNameInputSettings,
  };
}

export function resetNameInputSettingsForTests() {
  useGlobalNameInputSettings.value = true;
  capitalizeNameField.value = DEFAULTS.capitalizeNameField;
  hasLoaded = false;
  loadPromise = null;
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
}
